import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight,
  User,
  Briefcase,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calendar,
  Download,
  Printer,
  CheckCircle,
  Clock,
  Building
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';

const SalaryDetailsPage = () => {
  const navigate = useNavigate();
  const { employeeId } = useParams();
  const [employee, setEmployee] = useState(null);
  const [currentSalary, setCurrentSalary] = useState(null);
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Mock data - سيتم استبداله بـ API
  useEffect(() => {
    setTimeout(() => {
      const mockEmployee = {
        id: employeeId,
        name: 'أحمد محمد',
        employeeNumber: '1001',
        position: 'محامي أول',
        department: 'القسم القانوني',
        status: 'active',
        joinDate: '2020-01-15'
      };

      const mockCurrentSalary = {
        month: '2024-12',
        basicSalary: 15000,
        housingAllowance: 3000,
        transportAllowance: 1000,
        otherAllowances: 500,
        bonuses: 2000,
        deductions: 500,
        netSalary: 21000,
        status: 'paid',
        paymentDate: '2024-12-01',
        attendanceDays: 22,
        absenceDays: 0,
        lateCount: 1
      };

      const mockSalaryHistory = [
        {
          month: '2024-11',
          netSalary: 20500,
          status: 'paid',
          paymentDate: '2024-11-01'
        },
        {
          month: '2024-10',
          netSalary: 19000,
          status: 'paid',
          paymentDate: '2024-10-01'
        },
        {
          month: '2024-09',
          netSalary: 20000,
          status: 'paid',
          paymentDate: '2024-09-01'
        }
      ];

      setEmployee(mockEmployee);
      setCurrentSalary(mockCurrentSalary);
      setSalaryHistory(mockSalaryHistory);
      setLoading(false);
    }, 500);
  }, [employeeId]);

  const getStatusBadge = (status) => {
    return status === 'active' ? (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-green-50 text-green-700 border border-green-200">
        <CheckCircle className="w-3 h-3" />
        على رأس العمل
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-gray-50 text-gray-700 border border-gray-200">
        <Clock className="w-3 h-3" />
        موقوف
      </span>
    );
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    alert('سيتم تحميل بيان الراتب كملف PDF قريباً');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">جاري تحميل البيانات...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!employee || !currentSalary) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-600">لم يتم العثور على البيانات</p>
        </div>
      </AdminLayout>
    );
  }

  const totalAllowances = currentSalary.housingAllowance + currentSalary.transportAllowance + currentSalary.otherAllowances + currentSalary.bonuses;
  const totalEarnings = currentSalary.basicSalary + totalAllowances;

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/finance/salaries')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#1E3A8A] mb-4 transition-colors"
          >
            <ArrowRight className="w-5 h-5" />
            <span className="font-semibold">العودة للرواتب</span>
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-black text-[#1E3A8A] mb-2">تفاصيل راتب الموظف</h1>
              <p className="text-gray-600">عرض البيانات المالية الكاملة للموظف</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handlePrint}
                className="inline-flex items-center gap-2 px-4 py-2 border-2 border-gray-400 text-gray-700 rounded-lg font-bold hover:bg-gray-50 transition-all"
              >
                <Printer className="w-5 h-5" />
                <span>طباعة</span>
              </button>
              <button
                onClick={handleDownloadPDF}
                className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#1E3A8A] text-[#1E3A8A] rounded-lg font-bold hover:bg-blue-50 transition-all"
              >
                <Download className="w-5 h-5" />
                <span>تحميل PDF</span>
              </button>
            </div>
          </div>
        </div>

        {/* Employee Header */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-[#1E3A8A] rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-black text-gray-800">{employee.name}</h2>
                <div className="flex items-center gap-4 mt-2">
                  <span className="text-sm text-gray-600">الرقم الوظيفي: <span className="font-bold">{employee.employeeNumber}</span></span>
                  <span className="text-sm text-gray-600">المسمى: <span className="font-bold">{employee.position}</span></span>
                  <span className="text-sm text-gray-600">القسم: <span className="font-bold">{employee.department}</span></span>
                </div>
              </div>
            </div>
            <div>
              {getStatusBadge(employee.status)}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Salary Details */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <h3 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#1E3A8A]" />
              بيانات الراتب لشهر {currentSalary.month}
            </h3>

            <div className="space-y-4">
              {/* Basic Salary */}
              <div className="flex items-center justify-between py-3 border-b border-gray-200">
                <span className="text-gray-700 font-semibold">الراتب الأساسي</span>
                <span className="text-lg font-bold text-gray-800">{currentSalary.basicSalary.toLocaleString()} ر.س</span>
              </div>

              {/* Allowances */}
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  <span className="font-bold text-green-800">البدلات والمكافآت</span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">بدل السكن</span>
                    <span className="text-sm font-semibold text-green-700">{currentSalary.housingAllowance.toLocaleString()} ر.س</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">بدل النقل</span>
                    <span className="text-sm font-semibold text-green-700">{currentSalary.transportAllowance.toLocaleString()} ر.س</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">بدلات أخرى</span>
                    <span className="text-sm font-semibold text-green-700">{currentSalary.otherAllowances.toLocaleString()} ر.س</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">المكافآت</span>
                    <span className="text-sm font-semibold text-green-700">{currentSalary.bonuses.toLocaleString()} ر.س</span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-green-300">
                    <span className="text-sm font-bold text-gray-700">إجمالي البدلات</span>
                    <span className="text-sm font-black text-green-700">{totalAllowances.toLocaleString()} ر.س</span>
                  </div>
                </div>
              </div>

              {/* Deductions */}
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingDown className="w-5 h-5 text-red-600" />
                  <span className="font-bold text-red-800">الخصومات</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">إجمالي الخصومات</span>
                  <span className="text-sm font-black text-red-700">{currentSalary.deductions.toLocaleString()} ر.س</span>
                </div>
              </div>

              {/* Net Salary */}
              <div className="bg-[#1E3A8A] rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-white font-bold text-lg">صافي الراتب</span>
                  <span className="text-white font-black text-2xl">{currentSalary.netSalary.toLocaleString()} ر.س</span>
                </div>
              </div>
            </div>
          </div>

          {/* Attendance & Link */}
          <div className="space-y-6">
            {/* Attendance */}
            <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
              <h3 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#1E3A8A]" />
                الحضور والانصراف
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">أيام الحضور</span>
                  <span className="text-lg font-bold text-green-600">{currentSalary.attendanceDays}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">أيام الغياب</span>
                  <span className="text-lg font-bold text-red-600">{currentSalary.absenceDays}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">عدد التأخيرات</span>
                  <span className="text-lg font-bold text-yellow-600">{currentSalary.lateCount}</span>
                </div>
              </div>
            </div>

            {/* Payment Info */}
            <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
              <h3 className="text-lg font-black text-gray-800 mb-4">معلومات الصرف</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-600 block mb-1">الحالة</span>
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-green-50 text-green-700 border border-green-200">
                    <CheckCircle className="w-3 h-3" />
                    مدفوع
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-600 block mb-1">تاريخ الصرف</span>
                  <span className="text-sm font-bold text-gray-800">{currentSalary.paymentDate}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Salary History */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
          <h3 className="text-lg font-black text-gray-800 mb-4">سجل الرواتب السابقة</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">الشهر</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">صافي الراتب</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">الحالة</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">تاريخ الصرف</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">الإجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {salaryHistory.map((record, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className="font-semibold text-gray-800">{record.month}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-bold text-[#1E3A8A]">{record.netSalary.toLocaleString()} ر.س</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-green-50 text-green-700 border border-green-200">
                        <CheckCircle className="w-3 h-3" />
                        مدفوع
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700">{record.paymentDate}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => alert('عرض تفاصيل هذا الشهر')}
                        className="text-blue-600 hover:text-blue-800 font-semibold text-sm transition-colors"
                      >
                        عرض التفاصيل
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SalaryDetailsPage;
